import { Viewer } from '@react-pdf-viewer/core';
import {
  RenderCurrentPageLabelProps,
  pageNavigationPlugin,
} from '@react-pdf-viewer/page-navigation';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { twMerge } from 'tailwind-merge';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillCheckbox } from '../../../general/Dill/DillCheckbox';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import moment from 'moment';
import AppDatePicker, { parseDateValue } from '../CreditApplicationComponents/AppDatePicker';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { AppConfirmationModal } from '../../../general/AppConfirmationModal/AppConfirmationModal';
import successChecksCircle from '../../../../assets/svg/success_checks_circle.svg';
import errorCircle from '../../../../assets/svg/error_circle.svg';
import sendCircle from '../../../../assets/svg/send_circle.svg';
import { AppSuccessModal } from '../../../general/AppSuccessModal/AppSuccessModal';
import { useNavigate } from 'react-router-dom';
import { Upload } from 'react-feather';
import { COLORS } from '../../../../utils/colors';
import { CheckCircle, FileArrowUp, PlusCircle, Trash, Warning, X } from 'phosphor-react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import {
  BankReference,
  CreditApplicationFromFieldType,
  ExtractedBankReference,
  ExtractedCreditApplicationFields,
  ExtractedTradeReference,
} from '@dill/dill-shared';
import { createManualUploadCreditApplication } from '../../../../redux/services/creditApplicationsService';
import StateDropDown from '../CreditApplicationComponents/StateDropDown';
import BankDetailsModal from '../CreditApplicationComponents/BankDetailsModal';
import { firebaseLargeFileUploadHelper, buildSupplierPath } from '../../../../utils/helpers';
import ShowBankReferenceDetails from '../CreditApplicationComponents/ShowBankReferenceDetails';
import { verifyAddressFieldsOnSubmit } from '../CreditApplicationComponents/verifyAddressFieldsOnSubmit';
import AddressAutocompleteSelect from '../CreditApplicationComponents/AddressAutocompleteSelect';
import { BASE_API_URL } from '../../../../utils/constants';

interface TradeReference {
  id: string;
  tradeReferenceCompanyName: string;
  tradeReferenceCompanyEmail: string;
  tradeReferenceCompanyPhoneNumber: string;
  tradeReferenceCompanyAddress: string;
  tradeReferenceCompanyCity: string;
  tradeReferenceCompanyState: string;
  tradeReferenceCompanyZip: string;
}
const CreditApplicationManualUpload = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedPdfFile, setSelectedPdfFile] = useState<{ name: string; url: string } | null>(
    null
  );
  const [tradeReferences, setTradeReferences] = useState<TradeReference[]>([]);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [isConfirmAddCreditModalOpen, setIsConfirmAddCreditModalOpen] = useState(false);
  const [isErrorAddingCreditModalOpen, setIsErrorAddingCreditModalOpen] = useState(false);
  const [isAddingCreditSuccessModalOpen, setIsAddingCreditSuccessModalOpen] = useState(false);
  const [applicationType, setApplicationType] = useState<'CREDIT_APPLICATION' | 'CASH_APPLICATION'>(
    'CREDIT_APPLICATION'
  );
  const [isBankDetailsModalOpen, setIsBankDetailsModalOpen] = useState(false);
  const [editingBankReferenceIndex, setEditingBankReferenceIndex] = useState<number | null>(null);
  const [applicationDate, setApplicationDate] = useState<string>(
    moment().format('MM-DD-YYYY hh:mm A')
  );
  const [requestedAmount, setRequestedAmount] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyEmail, setCompanyEmail] = useState<string>('');
  const [companyAddress, setCompanyAddress] = useState<string>('');
  const [companyState, setCompanyState] = useState<string>('');
  const [companyCity, setCompanyCity] = useState<string>('');
  const [companyZip, setCompanyZip] = useState<string>('');
  const [companyEIN, setCompanyEIN] = useState<string>('');
  const [shippingAddress, setShippingAddress] = useState<string>('');
  const [shippingCity, setShippingCity] = useState<string>('');
  const [shippingState, setShippingState] = useState<string>('');
  const [shippingZip, setShippingZip] = useState<string>('');
  const [sameAsCompanyAddress, setSameAsCompanyAddress] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [bankReference, setBankReference] = useState<BankReference[] | null>(null);
  const [applicationDateTouched, setApplicationDateTouched] = useState<boolean>(false);
  const [extractionStatus, setExtractionStatus] = useState<
    'idle' | 'uploading' | 'extracting' | 'done' | 'error'
  >('idle');
  const [extractionWarning, setExtractionWarning] = useState<string | null>(null);
  const [extractionErrorMessage, setExtractionErrorMessage] = useState<string | null>(null);

  const latestFormStateRef = useRef({
    companyName,
    companyEmail,
    companyAddress,
    companyCity,
    companyState,
    companyZip,
    companyEIN,
    shippingAddress,
    shippingCity,
    shippingState,
    shippingZip,
    sameAsCompanyAddress,
    requestedAmount,
    applicationType,
    applicationDateTouched,
    tradeReferences,
    bankReference,
  });
  useEffect(() => {
    latestFormStateRef.current = {
      companyName,
      companyEmail,
      companyAddress,
      companyCity,
      companyState,
      companyZip,
      companyEIN,
      shippingAddress,
      shippingCity,
      shippingState,
      shippingZip,
      sameAsCompanyAddress,
      requestedAmount,
      applicationType,
      applicationDateTouched,
      tradeReferences,
      bankReference,
    };
  });

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const zoomPluginInstance = zoomPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;
  const { CurrentPageLabel } = pageNavigationPluginInstance;

  const isValidEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  };

  const isValidEIN = (ein: string) => {
    const regex = /^\d{2}-\d{7}$/;
    return regex.test(ein.trim());
  };

  const formatEIN = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 9);
    return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
  };

  const isValidPhoneNumber = (phone: string) => {
    const regex = /^\d{3}-\d{3}-\d{4}$/;
    return regex.test(phone.trim());
  };

  const formatPhoneNumber = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return digits;
  };

  useEffect(() => {
    if (!sameAsCompanyAddress) return;
    setShippingAddress(companyAddress);
    setShippingCity(companyCity);
    setShippingState(companyState);
    setShippingZip(companyZip);
    setErrorFields((prev) =>
      prev.filter(
        (field) =>
          !['shippingAddress', 'shippingCity', 'shippingState', 'shippingZip'].includes(field)
      )
    );
  }, [sameAsCompanyAddress, companyAddress, companyCity, companyState, companyZip]);

  const handleToggleSameAsCompanyAddress = (checked: boolean) => {
    setSameAsCompanyAddress(checked);
    if (!checked) {
      setShippingAddress('');
      setShippingCity('');
      setShippingState('');
      setShippingZip('');
    }
  };

  const checkErrors = () => {
    const fields: string[] = [];
    if (!companyName || companyName.trim() === '') fields.push('companyName');
    if (!companyEmail || companyEmail.trim() === '') fields.push('companyEmail');
    else if (!isValidEmail(companyEmail)) fields.push('companyEmail');
    if (!companyAddress || companyAddress.trim() === '') fields.push('companyAddress');
    if (!companyCity || companyCity.trim() === '') fields.push('companyCity');
    if (!companyState || companyState.trim() === '') fields.push('companyState');
    if (!companyZip || companyZip.trim() === '') fields.push('companyZip');
    if (!applicationDate || applicationDate.trim() === '') fields.push('applicationDate');
    if (!shippingAddress || shippingAddress.trim() === '') fields.push('shippingAddress');
    if (!shippingCity || shippingCity.trim() === '') fields.push('shippingCity');
    if (!shippingState || shippingState.trim() === '') fields.push('shippingState');
    if (!shippingZip || shippingZip.trim() === '') fields.push('shippingZip');
    if (companyEIN.trim() && !isValidEIN(companyEIN)) fields.push('companyEIN');
    if (
      (requestedAmount === null || requestedAmount === undefined || requestedAmount === 0) &&
      applicationType === 'CREDIT_APPLICATION'
    ) {
      fields.push('requestedAmount');
    }
    if (!selectedPdfFile) fields.push('selectFile');

    tradeReferences.forEach((ref) => {
      if (!ref.tradeReferenceCompanyName.trim()) fields.push(`tradeReferenceCompanyName-${ref.id}`);
      if (ref.tradeReferenceCompanyEmail.trim() && !isValidEmail(ref.tradeReferenceCompanyEmail)) {
        fields.push(`tradeReferenceCompanyEmail-${ref.id}`);
      }
      if (
        ref.tradeReferenceCompanyPhoneNumber.trim() &&
        !isValidPhoneNumber(ref.tradeReferenceCompanyPhoneNumber)
      ) {
        fields.push(`tradeReferenceCompanyPhoneNumber-${ref.id}`);
      }
    });

    setErrorFields(fields);
    return fields;
  };

  const generateFields = ({
    stepName,
    fromField,
    name,
    label,
    value,
    groupStepPosition,
    stepNumber,
    groupPosition = 0,
    groupName = '',
    groupColumnType,
    index = 0,
    sendEmail,
    isFieldEmail = false,
  }: {
    stepName: string;
    stepNumber: number;
    fromField: CreditApplicationFromFieldType;
    name: string;
    label?: string;
    value: string | number | boolean | null;
    groupStepPosition: number;
    groupPosition?: number;
    groupName?: string;
    groupColumnType?: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
    index?: number;
    sendEmail?: boolean;
    isFieldEmail?: boolean;
  }) => {
    return {
      uniqueId: `${stepName} ${groupName} ${groupPosition} ${name}`
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-'),
      id: name.trim().toLowerCase().replace(/\s+/g, '-'),
      name: label ?? name,
      fromField: fromField,
      isRequired: true,
      isHashed: false,
      stepName: stepName,
      stepNumber: stepNumber,
      groupStepPosition: groupStepPosition,
      groupedPosition: groupPosition,
      groupName: groupName,
      isFieldArray: false,
      isFieldEmail,
      value: value,
      index: index ?? 0,
      groupColumnType: groupColumnType ?? ('ONE_COLUMN' as const),
      sendEmail: sendEmail ?? false,
    };
  };

  function camelToCapitalized(text: string): string {
    if (!text) return '';

    const spaced = text.replace(/([A-Z])/g, ' $1').trim();
    return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const handleAddBankReference = (newRef: BankReference) => {
    setBankReference((prev) => [...(prev ?? []), newRef]);
  };

  const applyExtractedFields = (
    fields: ExtractedCreditApplicationFields,
    extractedTradeReferences: ExtractedTradeReference[],
    extractedBankReference: ExtractedBankReference | null
  ) => {
    const latest = latestFormStateRef.current;

    if (!latest.companyName.trim() && fields.companyName) {
      setCompanyName(fields.companyName.trim());
      setErrorFields((prev) => prev.filter((field) => field !== 'companyName'));
    }
    if (!latest.companyEmail.trim() && fields.companyEmail) {
      setCompanyEmail(fields.companyEmail.trim().toLowerCase());
      setErrorFields((prev) => prev.filter((field) => field !== 'companyEmail'));
    }
    if (!latest.companyAddress.trim() && fields.companyAddress) {
      setCompanyAddress(fields.companyAddress.trim());
      setErrorFields((prev) => prev.filter((field) => field !== 'companyAddress'));
    }
    if (!latest.companyCity.trim() && fields.companyCity) {
      setCompanyCity(fields.companyCity.trim());
      setErrorFields((prev) => prev.filter((field) => field !== 'companyCity'));
    }
    if (!latest.companyState.trim() && fields.companyState) {
      setCompanyState(fields.companyState.trim());
      setErrorFields((prev) => prev.filter((field) => field !== 'companyState'));
    }
    if (!latest.companyZip.trim() && fields.companyZip) {
      setCompanyZip(fields.companyZip.trim());
      setErrorFields((prev) => prev.filter((field) => field !== 'companyZip'));
    }
    if (!latest.companyEIN.trim() && fields.companyEIN) {
      setCompanyEIN(formatEIN(fields.companyEIN));
    }
    if (!latest.sameAsCompanyAddress) {
      if (!latest.shippingAddress.trim() && fields.shippingAddress) {
        setShippingAddress(fields.shippingAddress.trim());
        setErrorFields((prev) => prev.filter((field) => field !== 'shippingAddress'));
      }
      if (!latest.shippingCity.trim() && fields.shippingCity) {
        setShippingCity(fields.shippingCity.trim());
        setErrorFields((prev) => prev.filter((field) => field !== 'shippingCity'));
      }
      if (!latest.shippingState.trim() && fields.shippingState) {
        setShippingState(fields.shippingState.trim());
        setErrorFields((prev) => prev.filter((field) => field !== 'shippingState'));
      }
      if (!latest.shippingZip.trim() && fields.shippingZip) {
        setShippingZip(fields.shippingZip.trim());
        setErrorFields((prev) => prev.filter((field) => field !== 'shippingZip'));
      }
    }
    if (
      (latest.requestedAmount === null || latest.requestedAmount === undefined) &&
      fields.requestedAmount &&
      latest.applicationType === 'CREDIT_APPLICATION'
    ) {
      setRequestedAmount(Math.round(fields.requestedAmount));
      setErrorFields((prev) => prev.filter((field) => field !== 'requestedAmount'));
    }
    if (!latest.applicationDateTouched && fields.applicationDate) {
      const parsedDate = moment(fields.applicationDate, ['YYYY-MM-DD', moment.ISO_8601], true);
      if (parsedDate.isValid()) {
        const now = moment();
        const combined = parsedDate.set({
          hour: now.hour(),
          minute: now.minute(),
          second: now.second(),
        });
        setApplicationDate(combined.format('MM-DD-YYYY hh:mm A'));
        setErrorFields((prev) => prev.filter((field) => field !== 'applicationDate'));
      }
    }
    if (latest.tradeReferences.length === 0 && extractedTradeReferences.length > 0) {
      setTradeReferences(
        extractedTradeReferences.slice(0, 10).map((ref, index) => ({
          id: `tr-${Math.floor(Math.random() * 1000000)}-${Date.now()}-${index}`,
          tradeReferenceCompanyName: ref.companyName?.trim() ?? '',
          tradeReferenceCompanyEmail: ref.email?.trim().toLowerCase() ?? '',
          tradeReferenceCompanyPhoneNumber: ref.phone ? formatPhoneNumber(ref.phone) : '',
          tradeReferenceCompanyAddress: ref.address?.trim() ?? '',
          tradeReferenceCompanyCity: ref.city?.trim() ?? '',
          tradeReferenceCompanyState: ref.state?.trim() ?? '',
          tradeReferenceCompanyZip: ref.zip?.trim() ?? '',
        }))
      );
    }
    if (
      (!latest.bankReference || latest.bankReference.length === 0) &&
      extractedBankReference?.bankName
    ) {
      handleAddBankReference({
        connectionType: 'manual',
        bankDetails: {
          bankName: extractedBankReference.bankName.trim(),
          accessToken: '',
          accountNumber: extractedBankReference.accountNumber?.trim() ?? '',
          contactPerson: extractedBankReference.contactPerson?.trim() ?? '',
          phoneNumber: extractedBankReference.phoneNumber
            ? formatPhoneNumber(extractedBankReference.phoneNumber)
            : '',
          address: extractedBankReference.address?.trim() ?? '',
          city: extractedBankReference.city?.trim() ?? '',
          state: extractedBankReference.state?.trim() ?? '',
          zip: extractedBankReference.zip?.trim() ?? '',
          email: extractedBankReference.email?.trim().toLowerCase() ?? '',
        },
        authorizationFormPdf: null,
      });
    }
  };

  const handleExtractFromPdf = async (file: File) => {
    if (!currentSupplier?.id) return;
    setExtractionStatus('uploading');
    setExtractionWarning(null);
    setExtractionErrorMessage(null);

    try {
      const uploadUrl = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(file),
        fileNameWithExtension: file.name,
        storagePath: `creditApplicationFiles/${currentSupplier.id}/extraction/${Date.now()}`,
      });
      if (!uploadUrl) throw new Error('Upload failed. Please try again.');

      setExtractionStatus('extracting');

      const response = await fetch(
        `${BASE_API_URL}/creditApplications/extractCreditApplicationFields`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileUrl: uploadUrl,
            mimeType: file.type,
            supplierId: currentSupplier.id,
          }),
        }
      );
      const json = await response.json();

      if (!response.ok || json.errors) {
        throw new Error(json?.errors?.[0]?.msg || 'Extraction failed. Please try again.');
      }

      const fields: ExtractedCreditApplicationFields | undefined = json.data?.fields;
      const extractedTradeReferences: ExtractedTradeReference[] = json.data?.tradeReferences || [];
      const extractedBankReference: ExtractedBankReference | null =
        json.data?.bankReference || null;

      if (fields) {
        applyExtractedFields(fields, extractedTradeReferences, extractedBankReference);
      }

      setExtractionWarning(json.data?.warning || null);
      setExtractionStatus('done');
    } catch (error: any) {
      console.error('Credit application extraction error:', error);
      setExtractionErrorMessage(error.message || 'Something went wrong. Please try again.');
      setExtractionStatus('error');
    }
  };

  const handleFileSelected = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String: any = reader.result;
      setSelectedPdfFile({ url: base64String, name: file.name });
      setErrorFields((prev) => prev.filter((field) => field !== 'selectFile'));
    };
    reader.readAsDataURL(file);
    handleExtractFromPdf(file);
  };

  const handleRemoveSelectedPdf = () => {
    setSelectedPdfFile(null);
    setExtractionStatus('idle');
    setExtractionWarning(null);
    setExtractionErrorMessage(null);
  };

  const {
    getRootProps: getPdfDropzoneRootProps,
    getInputProps: getPdfDropzoneInputProps,
    isDragActive: isPdfDragActive,
  } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    onDrop: (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        handleFileSelected(file);
      }
    },
  });

  const handleSubmitCreditApp = async () => {
    const firstName = companyName.split(' ')[0];
    const lastName = companyName?.split(' ')?.slice(1)?.join(' ') ?? '';
    const groupedTradeReferences = tradeReferences.map((item) =>
      Object.entries(item)
        .filter(([key]) => key !== 'id')
        .map(([key, value]) => ({
          name: key,
          value,
        }))
    );
    const tradeReferenceFields = groupedTradeReferences.flatMap((item, index) => {
      return item.map((field) => {
        return generateFields({
          stepName: 'Trade Reference',
          fromField: 'TEXT' as const,
          name: camelToCapitalized(field.name),
          value: field.value,
          groupStepPosition: 0,
          stepNumber: 2,
          groupName: 'Trade Reference',
          groupPosition: index,
          sendEmail: true,
          isFieldEmail: field.name.toLowerCase().includes('email'),
          index: field.name.toLowerCase().includes('address')
            ? 1 + index
            : field.name.toLowerCase().includes('city') ||
              field.name.toLowerCase().includes('state') ||
              field.name.toLowerCase().includes('zip')
            ? 2 + index
            : 0 + index,
          groupColumnType: field.name.toLowerCase().includes('address')
            ? ('ONE_COLUMN' as const)
            : field.name.toLowerCase().includes('city') ||
              field.name.toLowerCase().includes('state') ||
              field.name.toLowerCase().includes('zip')
            ? ('THREE_COLUMNS' as const)
            : ('TWO_COLUMNS' as const),
        });
      });
    });

    const fieldValues = [
      generateFields({
        stepName: 'Personal Information',
        fromField: 'TEXT' as const,
        name: 'First Name Personal',
        label: 'First Name',
        value: firstName,
        groupStepPosition: 0,
        stepNumber: 0,
        groupColumnType: 'TWO_COLUMNS' as const,
      }),
      generateFields({
        stepName: 'Personal Information',
        fromField: 'TEXT' as const,
        name: 'Last Name Personal',
        label: 'Last Name',
        value: lastName,
        groupStepPosition: 0,
        stepNumber: 0,
        groupColumnType: 'TWO_COLUMNS' as const,
      }),
      generateFields({
        stepName: 'Personal Information',
        fromField: 'TEXT' as const,
        name: 'Email Personal',
        label: 'Email',
        value: companyEmail,
        groupStepPosition: 1,
        stepNumber: 0,
        isFieldEmail: true,
      }),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Customer or Company Name',
        value: companyName,
        groupStepPosition: 0,
        stepNumber: 1,
      }),
      ...(applicationType === 'CREDIT_APPLICATION'
        ? [
            generateFields({
              stepName: 'Business Information',
              fromField: 'TEXT' as const,
              name: 'Amount of Credit Requested',
              value: requestedAmount,
              groupStepPosition: 1,
              stepNumber: 1,
            }),
          ]
        : []),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Company Address',
        value: companyAddress,
        groupStepPosition: 2,
        stepNumber: 1,
      }),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Company City',
        value: companyCity,
        groupStepPosition: 3,
        stepNumber: 1,
        groupColumnType: 'THREE_COLUMNS' as const,
      }),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Company State',
        value: companyState,
        groupStepPosition: 3,
        stepNumber: 1,
        groupColumnType: 'THREE_COLUMNS' as const,
      }),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Company Zip',
        value: companyZip,
        groupStepPosition: 3,
        stepNumber: 1,
        groupColumnType: 'THREE_COLUMNS' as const,
      }),
      ...(companyEIN.trim()
        ? [
            generateFields({
              stepName: 'Business Information',
              fromField: 'TEXT' as const,
              name: 'Company EIN',
              value: companyEIN.trim(),
              groupStepPosition: 4,
              stepNumber: 1,
            }),
          ]
        : []),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Shipping Address',
        value: shippingAddress,
        groupStepPosition: 5,
        stepNumber: 1,
      }),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Shipping City',
        value: shippingCity,
        groupStepPosition: 6,
        stepNumber: 1,
        groupColumnType: 'THREE_COLUMNS' as const,
      }),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Shipping State',
        value: shippingState,
        groupStepPosition: 6,
        stepNumber: 1,
        groupColumnType: 'THREE_COLUMNS' as const,
      }),
      generateFields({
        stepName: 'Business Information',
        fromField: 'TEXT' as const,
        name: 'Shipping Zip',
        value: shippingZip,
        groupStepPosition: 6,
        stepNumber: 1,
        groupColumnType: 'THREE_COLUMNS' as const,
      }),
      ...tradeReferenceFields,
      ...(bankReference && bankReference.length > 0
        ? [
            generateFields({
              stepName: 'Bank Reference',
              fromField: 'BANK_REFERENCE' as const,
              name: 'Bank Reference',
              value: 'finance',
              groupStepPosition: 0,
              stepNumber: 4,
            }),
          ]
        : []),
    ];
    const verifiedFieldValues = await verifyAddressFieldsOnSubmit(fieldValues);
    const result = await dispatch(
      createManualUploadCreditApplication({
        supplierId: currentSupplier?.id ?? '',
        applicationType,
        templateId: '',
        status: isCompleted ? ('APPROVED' as const) : ('SUBMITTED' as const),
        senderEmail: companyEmail,
        requestedDate: applicationDate,
        fieldValues: verifiedFieldValues,
        selectedPdfFile: selectedPdfFile,
        invitedSignatures: [],
        bankReference,
      })
    );
    if (result.type === 'creditApplications/createManualUploadCreditApplication/fulfilled') {
      setIsAddingCreditSuccessModalOpen(true);
    } else {
      setIsErrorAddingCreditModalOpen(true);
    }
  };

  const handleAddTradeReference = () => {
    const id = `tr-${Math.floor(Math.random() * 1000000)}-${Date.now()}`;
    setTradeReferences((prev) => [
      ...prev,
      {
        id,
        tradeReferenceCompanyName: '',
        tradeReferenceCompanyEmail: '',
        tradeReferenceCompanyPhoneNumber: '',
        tradeReferenceCompanyAddress: '',
        tradeReferenceCompanyCity: '',
        tradeReferenceCompanyState: '',
        tradeReferenceCompanyZip: '',
      },
    ]);
  };

  const updateTradeField = (id: string, field: keyof TradeReference, value: string) => {
    setTradeReferences((prev) =>
      prev.map((ref) => (ref.id === id ? { ...ref, [field]: value } : ref))
    );

    setErrorFields((prev) => prev.filter((err) => err !== `${field}-${id}`));
  };

  const handleAddBankDetails = async (details: {
    bankName: string;
    accountNumber: string;
    contactPerson: string;
    phoneNumber: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    email: string;
    authorizedFile?: File | null;
  }) => {
    let authorizedFile: { name: string; url: string; fileType: string } | null = null;
    if (details.authorizedFile) {
      const fileType = details.authorizedFile.name.split('.').pop() || '';
      const uploadURL = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(details.authorizedFile),
        fileNameWithExtension: `attachment-${Date.now()}.${fileType}`,
        storagePath: `creditApplicationFiles/${currentSupplier?.id}/authorizedFile/${Date.now()}`,
      });
      if (uploadURL) {
        authorizedFile = {
          name: details.authorizedFile.name,
          url: uploadURL,
          fileType,
        };
      }
    }

    const bankDetails = {
      bankName: details.bankName,
      accessToken: '',
      accountNumber: details.accountNumber,
      contactPerson: details.contactPerson,
      phoneNumber: details.phoneNumber,
      address: details.address,
      city: details.city,
      state: details.state,
      zip: details.zip,
      email: details.email,
    };

    if (editingBankReferenceIndex !== null) {
      setBankReference((prev) => {
        if (!prev) return prev;
        return prev.map((ref, index) =>
          index === editingBankReferenceIndex
            ? {
                ...ref,
                bankDetails,
                authorizationFormPdf: authorizedFile ?? ref.authorizationFormPdf,
              }
            : ref
        );
      });
      setEditingBankReferenceIndex(null);
      return;
    }

    handleAddBankReference({
      connectionType: 'manual',
      bankDetails,
      authorizationFormPdf: authorizedFile,
    });
  };

  const handleEditBankReference = (index: number) => {
    setEditingBankReferenceIndex(index);
    setIsBankDetailsModalOpen(true);
  };

  const handleRemoveBankReference = (index: number) => {
    if (!bankReference) return;
    setBankReference((prev) => {
      if (!prev) return null;
      const updated = prev.filter((_, i) => i !== index);
      return updated.length > 0 ? updated : null;
    });
  };

  const renderTradeReference = (reference: any, index: number) => {
    return (
      <div key={reference.id} className="border-b pb-2 GREY_300-BORDER">
        <div className="my-2 w-full flex gap-4 mb-3">
          <AppInputField
            isRequired={true}
            label="Company Name"
            value={reference.tradeReferenceCompanyName}
            errorText={
              errorFields.includes(`tradeReferenceCompanyName-${reference.id}`)
                ? 'Company Name is required'
                : ''
            }
            onTextChange={(text) => {
              updateTradeField(reference.id, 'tradeReferenceCompanyName', text);
            }}
            onBlur={() => {
              updateTradeField(
                reference.id,
                'tradeReferenceCompanyName',
                reference.tradeReferenceCompanyName.trim()
              );
            }}
          />
        </div>
        <div className="my-2 w-full flex gap-4 mb-3">
          <AppInputField
            label="Credit or A/R Email"
            value={reference.tradeReferenceCompanyEmail}
            errorText={
              errorFields.includes(`tradeReferenceCompanyEmail-${reference.id}`)
                ? 'Enter a valid email'
                : ''
            }
            onTextChange={(text) => {
              updateTradeField(reference.id, 'tradeReferenceCompanyEmail', text);
            }}
            onBlur={() => {
              updateTradeField(
                reference.id,
                'tradeReferenceCompanyEmail',
                reference.tradeReferenceCompanyEmail.trim().toLowerCase()
              );
            }}
          />

          <AppInputField
            label="Credit or A/R Phone Number"
            placeholder="XXX-XXX-XXXX"
            maxLength={12}
            value={reference.tradeReferenceCompanyPhoneNumber}
            errorText={
              errorFields.includes(`tradeReferenceCompanyPhoneNumber-${reference.id}`)
                ? 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)'
                : ''
            }
            onTextChange={(text) => {
              updateTradeField(
                reference.id,
                'tradeReferenceCompanyPhoneNumber',
                formatPhoneNumber(text)
              );
            }}
            onBlur={() => {
              updateTradeField(
                reference.id,
                'tradeReferenceCompanyPhoneNumber',
                reference.tradeReferenceCompanyPhoneNumber.trim()
              );
            }}
          />
        </div>

        <div className="my-2 w-full flex gap-4 mb-3">
          <AddressAutocompleteSelect
            label="Company Address"
            value={reference.tradeReferenceCompanyAddress}
            onTextChange={(text) => {
              updateTradeField(reference.id, 'tradeReferenceCompanyAddress', text);
            }}
            onSelectSuggestion={(suggestion) => {
              const streetLine = [suggestion.streetLine, suggestion.secondary]
                .filter(Boolean)
                .join(' ');
              updateTradeField(reference.id, 'tradeReferenceCompanyAddress', streetLine);
              updateTradeField(reference.id, 'tradeReferenceCompanyCity', suggestion.city);
              updateTradeField(reference.id, 'tradeReferenceCompanyState', suggestion.state);
              updateTradeField(reference.id, 'tradeReferenceCompanyZip', suggestion.zipcode);
            }}
            onBlur={() => {
              updateTradeField(
                reference.id,
                'tradeReferenceCompanyAddress',
                reference.tradeReferenceCompanyAddress.trim()
              );
            }}
          />
        </div>
        <div className="my-2 w-full flex gap-4 mb-2">
          <AppInputField
            label="Company City"
            value={reference.tradeReferenceCompanyCity}
            onTextChange={(text) => {
              updateTradeField(reference.id, 'tradeReferenceCompanyCity', text);
            }}
            onBlur={() => {
              updateTradeField(
                reference.id,
                'tradeReferenceCompanyCity',
                reference.tradeReferenceCompanyCity.trim()
              );
            }}
          />
          <StateDropDown
            isRequired={false}
            label="Company State"
            value={reference.tradeReferenceCompanyState}
            onTextChange={(text) => {
              updateTradeField(reference.id, 'tradeReferenceCompanyState', text);
            }}
          />
          <AppInputField
            label="Company Zip"
            value={reference.tradeReferenceCompanyZip}
            onTextChange={(text) => {
              updateTradeField(reference.id, 'tradeReferenceCompanyZip', text);
            }}
            onBlur={() => {
              updateTradeField(
                reference.id,
                'tradeReferenceCompanyZip',
                reference.tradeReferenceCompanyZip.trim()
              );
            }}
          />
        </div>

        <div className="w-full flex flex-row-reverse">
          <div
            className="text-sm ERROR_500-CLR cursor-pointer flex gap-1 items-center mt-2"
            onClick={() => setTradeReferences((prev) => prev.filter((f) => f.id !== reference.id))}>
            <div className="text-sm">Remove</div>
            <Trash size={16} weight="bold" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-row h-full py-4 justify-between px-4">
      <BankDetailsModal
        open={isBankDetailsModalOpen}
        handleClose={() => {
          setIsBankDetailsModalOpen(false);
          setEditingBankReferenceIndex(null);
        }}
        onSubmit={handleAddBankDetails}
        bankReferenceRequiredFields={['contactPerson', 'phoneNumber', 'address', 'authorizedFile']}
        fieldsOptional
        initialValues={(() => {
          const editingBankDetails =
            editingBankReferenceIndex !== null
              ? bankReference?.[editingBankReferenceIndex]?.bankDetails
              : null;
          if (!editingBankDetails) return null;
          return {
            bankName: editingBankDetails.bankName,
            accountNumber: editingBankDetails.accountNumber,
            contactPerson: editingBankDetails.contactPerson,
            phoneNumber: editingBankDetails.phoneNumber,
            address: editingBankDetails.address,
            city: editingBankDetails.city,
            state: editingBankDetails.state,
            zip: editingBankDetails.zip,
            email: editingBankDetails.email ?? '',
          };
        })()}
        title={editingBankReferenceIndex !== null ? 'Edit Bank Details' : 'Add Bank Details'}
        submitButtonLabel={editingBankReferenceIndex !== null ? 'Save Changes' : 'Add Bank Details'}
      />
      <AppConfirmationModal
        open={isConfirmAddCreditModalOpen}
        modalImage={sendCircle}
        negativeButtonText="No, cancel"
        highlightedText={``}
        positiveButtonText="Yes, add it"
        title={`Are you sure you want to manually add this ${
          applicationType === 'CREDIT_APPLICATION' ? 'credit' : 'COD'
        } application?`}
        subTitle={`This will add the ${
          applicationType === 'CREDIT_APPLICATION' ? 'credit' : 'COD'
        } application to your account.`}
        handleClose={() => {
          setIsConfirmAddCreditModalOpen(false);
        }}
        onNegativeButtonClick={() => {
          setIsConfirmAddCreditModalOpen(false);
        }}
        onPositiveButtonClick={() => {
          setIsConfirmAddCreditModalOpen(false);
          handleSubmitCreditApp();
        }}
      />
      <AppSuccessModal
        open={isAddingCreditSuccessModalOpen}
        modalImage={successChecksCircle}
        highlightedText={``}
        title={`${applicationType === 'CREDIT_APPLICATION' ? 'Credit' : 'COD'} application added!`}
        subTitle={`This ${
          applicationType === 'CREDIT_APPLICATION' ? 'credit' : 'COD'
        } application has been successfully added to your account.`}
        buttonText={`View Applications`}
        handleClose={() => {
          setIsAddingCreditSuccessModalOpen(false);
          navigate(buildSupplierPath(currentSupplier?.id, `/creditApplications`));
        }}
      />
      <AppSuccessModal
        open={isErrorAddingCreditModalOpen}
        modalImage={errorCircle}
        title={`Adding your ${
          applicationType === 'CREDIT_APPLICATION' ? 'credit' : 'COD'
        } application failed!`}
        subTitle={`Failed to add your ${
          applicationType === 'CREDIT_APPLICATION' ? 'credit' : 'COD'
        } application. Contact customer support`}
        buttonText="Close"
        secondButtonText="Retry"
        isSecondButton={true}
        onSecondButtonClick={() => {
          setIsErrorAddingCreditModalOpen(false);
          handleSubmitCreditApp();
        }}
        handleClose={() => {
          setIsErrorAddingCreditModalOpen(false);
        }}
      />

      <div className="w-full flex justify-between bg-white px-3 py-4">
        <div className="flex flex-col w-5/12 h-[calc(100vh-140px)] overflow-scroll pr-3">
          <div className={twMerge('flex flex-col  bg-white px-4 py-3 mb-3')}>
            <div
              {...getPdfDropzoneRootProps()}
              aria-label="Upload credit application document. Click or drag and drop a PDF here"
              className={twMerge(
                'flex flex-col items-center justify-center PRIMARY_50-BG py-2 rounded-lg cursor-pointer border-2 border-dashed border-[#CBD5E1]',
                errorFields.includes('selectFile') ? 'border-red-600' : '',
                isPdfDragActive ? 'border-blue-400' : ''
              )}>
              <div className="h-12 w-12 my-1 rounded-full flex flex-col items-center justify-center PRIMARY_100-BG ">
                <FileArrowUp size={30} color={COLORS.PRIMARY_500} />
              </div>
              <div className="relative my-0.5 ">
                <input {...getPdfDropzoneInputProps()} />
                <DillButton
                  onClick={() => {}}
                  variant="secondary"
                  label="Upload Document"
                  icon={<Upload size={20} />}
                  height={36}
                />
              </div>
              <p className="text-xs text-gray-500">*Accepted File .pdf — click or drag and drop</p>
              {selectedPdfFile && (
                <div className="flex items-center gap-1.5 mt-4 mb-1">
                  <p className="text-sm text-center">{selectedPdfFile.name}</p>
                  <button
                    type="button"
                    aria-label="Remove uploaded file"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemoveSelectedPdf();
                    }}
                    className="inline-flex min-h-6 min-w-6 items-center justify-center rounded-md ERROR_500-CLR transition-colors hover:bg-red-50">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
            {errorFields.includes('selectFile') && (
              <p className="text-red-600 text-xs">Please select a file</p>
            )}
            {(extractionStatus === 'uploading' || extractionStatus === 'extracting') && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 rounded-lg border GREY_200-BORDER GREY_50-BG px-3 py-2 mt-2 text-sm GREY_600-CLR">
                <span className="h-3.5 w-3.5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                <span>
                  {extractionStatus === 'uploading'
                    ? 'Uploading your document…'
                    : 'Reading application details…'}
                </span>
              </div>
            )}
            {extractionStatus === 'done' && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 rounded-lg border SUCCESS_100-BG SUCCESS_200-BORDER SUCCESS_700-CLR px-3 py-2 mt-2 text-sm">
                <CheckCircle
                  size={17}
                  weight="fill"
                  color={COLORS.SUCCESS_600}
                  className="flex-shrink-0"
                />
                <span>
                  Filled in any empty fields from the uploaded document. Please review before
                  submitting.
                </span>
              </div>
            )}
            {extractionWarning && (
              <div
                role="status"
                className="flex items-start gap-2 rounded-lg border WARNING_200-BORDER WARNING_100-BG px-3 py-2 mt-2 text-sm WARNING_700-CLR">
                <Warning
                  size={17}
                  weight="fill"
                  color={COLORS.WARNING_600}
                  className="flex-shrink-0 mt-0.5"
                />
                <span>{extractionWarning}</span>
              </div>
            )}
            {extractionStatus === 'error' && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border ERROR_200-BORDER ERROR_100-BG px-3 py-2 mt-2 text-sm ERROR_600-CLR">
                <X
                  size={17}
                  weight="bold"
                  color={COLORS.ERROR_500}
                  className="flex-shrink-0 mt-0.5"
                />
                <span>
                  {extractionErrorMessage} You can still fill in the fields below manually.
                </span>
              </div>
            )}
          </div>
          <div className="my-2 w-full">
            <AppDropDown
              isRequired
              label="Application Type"
              value={applicationType}
              items={[
                { label: 'Credit Application', value: 'CREDIT_APPLICATION' },
                { label: 'COD Application', value: 'CASH_APPLICATION' },
              ]}
              onSelectChange={(item) => {
                if (!item?.value) return;
                setApplicationType(item.value as 'CREDIT_APPLICATION' | 'CASH_APPLICATION');
              }}
            />
          </div>
          <div className="my-2 w-full flex gap-4">
            <AppInputField
              isRequired={true}
              label="Company Name"
              value={companyName}
              errorText={errorFields.includes('companyName') ? 'Company Name is required' : ''}
              onTextChange={(text) => {
                setCompanyName(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'companyName'));
              }}
              onBlur={() => {
                setCompanyName(companyName.trim());
              }}
            />
            <AppInputField
              isRequired={true}
              label="Company Email"
              value={companyEmail}
              errorText={
                errorFields.includes('companyEmail')
                  ? companyEmail.trim()
                    ? 'Enter a valid email'
                    : 'Company Email is required'
                  : ''
              }
              onTextChange={(text) => {
                setCompanyEmail(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'companyEmail'));
              }}
              onBlur={() => {
                setCompanyEmail(companyEmail.trim().toLowerCase());
              }}
            />
          </div>
          <div className="my-2 w-full flex gap-4 mb-3">
            <AddressAutocompleteSelect
              isRequired={true}
              label="Company Address"
              value={companyAddress}
              errorText={
                errorFields.includes(`companyAddress`) ? 'Company Address is required' : ''
              }
              onTextChange={(text) => {
                setCompanyAddress(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'companyAddress'));
              }}
              onSelectSuggestion={(suggestion) => {
                const streetLine = [suggestion.streetLine, suggestion.secondary]
                  .filter(Boolean)
                  .join(' ');
                setCompanyAddress(streetLine);
                setCompanyCity(suggestion.city);
                setCompanyState(suggestion.state);
                setCompanyZip(suggestion.zipcode);
                setErrorFields((prev) =>
                  prev.filter(
                    (field) =>
                      !['companyAddress', 'companyCity', 'companyState', 'companyZip'].includes(
                        field
                      )
                  )
                );
              }}
              onBlur={() => {
                setCompanyAddress(companyAddress.trim());
              }}
            />
          </div>
          <div className="my-2 w-full flex gap-4 mb-2">
            <AppInputField
              isRequired={true}
              label="Company City"
              value={companyCity}
              errorText={errorFields.includes(`companyCity`) ? 'Company City is required' : ''}
              onTextChange={(text) => {
                setCompanyCity(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'companyCity'));
              }}
              onBlur={() => {
                setCompanyCity(companyCity.trim());
              }}
            />
            <StateDropDown
              isRequired={true}
              label="Company State"
              value={companyState}
              errorText={errorFields.includes(`companyState`) ? 'Company State is required' : ''}
              onTextChange={(text) => {
                setCompanyState(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'companyState'));
              }}
            />
            <AppInputField
              isRequired={true}
              label="Company Zip"
              value={companyZip}
              errorText={errorFields.includes(`companyZip`) ? 'Company Zip is required' : ''}
              onTextChange={(text) => {
                setCompanyZip(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'companyZip'));
              }}
              onBlur={() => {
                setCompanyZip(companyZip.trim());
              }}
            />
          </div>
          <div className="mb-2 font-semibold text-sm">Shipping Address</div>
          <div className="my-2 w-full flex gap-2 items-center">
            <DillCheckbox
              isChecked={sameAsCompanyAddress}
              onChange={(val: boolean) => handleToggleSameAsCompanyAddress(val)}
            />
            <p
              className="text-sm ml-1 cursor-pointer"
              onClick={() => handleToggleSameAsCompanyAddress(!sameAsCompanyAddress)}>
              Shipping address is same as company address
            </p>
          </div>
          <div className="my-2 w-full flex gap-4 mb-3">
            <AddressAutocompleteSelect
              isRequired={true}
              label="Shipping Address"
              value={shippingAddress}
              errorText={
                errorFields.includes('shippingAddress') ? 'Shipping Address is required' : ''
              }
              onTextChange={(text) => {
                setShippingAddress(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'shippingAddress'));
              }}
              onSelectSuggestion={(suggestion) => {
                const streetLine = [suggestion.streetLine, suggestion.secondary]
                  .filter(Boolean)
                  .join(' ');
                setShippingAddress(streetLine);
                setShippingCity(suggestion.city);
                setShippingState(suggestion.state);
                setShippingZip(suggestion.zipcode);
                setErrorFields((prev) =>
                  prev.filter(
                    (field) =>
                      !['shippingAddress', 'shippingCity', 'shippingState', 'shippingZip'].includes(
                        field
                      )
                  )
                );
              }}
              onBlur={() => setShippingAddress(shippingAddress.trim())}
            />
          </div>
          <div className="my-2 w-full flex gap-4 mb-2">
            <AppInputField
              isRequired={true}
              label="Shipping City"
              value={shippingCity}
              errorText={errorFields.includes('shippingCity') ? 'Shipping City is required' : ''}
              onTextChange={(text) => {
                setShippingCity(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'shippingCity'));
              }}
              onBlur={() => setShippingCity(shippingCity.trim())}
            />
            <StateDropDown
              isRequired={true}
              label="Shipping State"
              value={shippingState}
              errorText={errorFields.includes('shippingState') ? 'Shipping State is required' : ''}
              onTextChange={(text) => {
                setShippingState(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'shippingState'));
              }}
            />
            <AppInputField
              isRequired={true}
              label="Shipping Zip"
              value={shippingZip}
              errorText={errorFields.includes('shippingZip') ? 'Shipping Zip is required' : ''}
              onTextChange={(text) => {
                setShippingZip(text);
                setErrorFields((prev) => prev.filter((field) => field !== 'shippingZip'));
              }}
              onBlur={() => setShippingZip(shippingZip.trim())}
            />
          </div>
          <div className="my-2 w-full flex gap-4 mb-3">
            <AppInputField
              label="Company EIN"
              placeholder="XX-XXXXXXX"
              value={companyEIN}
              errorText={errorFields.includes('companyEIN') ? 'Enter a valid EIN (XX-XXXXXXX)' : ''}
              onTextChange={(text) => {
                setCompanyEIN(formatEIN(text));
                setErrorFields((prev) => prev.filter((field) => field !== 'companyEIN'));
              }}
              onBlur={() => setCompanyEIN(companyEIN.trim())}
              maxLength={10}
            />
          </div>
          <div className="my-2 w-full flex gap-4">
            <AppDatePicker
              isRequired={true}
              label="Application Date"
              selectedDate={parseDateValue(applicationDate)}
              errorText={
                errorFields.includes('applicationDate') ? 'Application Date is required' : ''
              }
              onDateSelected={(date) => {
                setApplicationDateTouched(true);
                const now = moment();
                const combined = moment(date).set({
                  hour: now.hour(),
                  minute: now.minute(),
                  second: now.second(),
                });
                setApplicationDate(combined.format('MM-DD-YYYY hh:mm A'));
              }}
            />
            {applicationType === 'CREDIT_APPLICATION' && (
              <AppInputField
                isRequired={true}
                label="Amount Requested"
                inputType="text"
                value={requestedAmount !== null ? requestedAmount.toLocaleString('en-US') : ''}
                errorText={
                  errorFields.includes('requestedAmount') ? 'Amount Requested is required' : ''
                }
                onTextChange={(text) => {
                  const digits = text.replace(/\D/g, '');
                  setRequestedAmount(digits ? parseInt(digits, 10) : null);
                  setErrorFields((prev) => prev.filter((field) => field !== 'requestedAmount'));
                }}
                prefix="$"
              />
            )}
          </div>
          {tradeReferences.length > 0 && (
            <div className="PRIMARY_100-BG p-4 rounded mt-2">
              <div className="mb-2 font-semibold text-sm">Trade Reference</div>
              {tradeReferences.map((reference, index) => renderTradeReference(reference, index))}
            </div>
          )}
          <div>
            <DillButton
              onClick={handleAddTradeReference}
              label="Add Trade Reference"
              icon={<PlusCircle size={16} />}
              width={160}
              height={36}
              className="mt-2"
            />
          </div>
          {bankReference && (
            <div className="PRIMARY_100-BG p-4 rounded mt-2">
              <ShowBankReferenceDetails
                bankReferences={bankReference}
                onRemove={handleRemoveBankReference}
                onEdit={handleEditBankReference}
              />
            </div>
          )}
          <div>
            <DillButton
              onClick={() => setIsBankDetailsModalOpen(true)}
              variant="secondary"
              label="Add Bank Reference"
              icon={<PlusCircle size={16} />}
              width={160}
              height={36}
              className="mt-2"
            />
          </div>
          <div className="mt-4 w-full flex gap-2 items-center">
            <DillCheckbox
              isChecked={isCompleted}
              onChange={(val: boolean) => setIsCompleted(val)}
            />
            <p className="text-sm ml-1 cursor-pointer" onClick={() => setIsCompleted(!isCompleted)}>
              I've already approved this application.
            </p>
          </div>
          {errorFields.length > 0 && (
            <p className="text-red-600 text-xs text-right bg-white mt-3">
              Please check the errors above
            </p>
          )}
          <div className="flex flex-row justify-end bg-white mt-2 gap-3">
            <DillButton
              onClick={() =>
                navigate(buildSupplierPath(currentSupplier?.id, `/creditApplications`))
              }
              variant="secondary"
              label="Close"
              width={100}
              className="flex items-center justify-center"
            />
            <DillButton
              onClick={() => {
                const fieldsWithErrors = checkErrors();
                if (fieldsWithErrors.length === 0) {
                  setIsConfirmAddCreditModalOpen(true);
                }
              }}
              label="Add"
              width={100}
              className="flex items-center justify-center"
            />
          </div>
        </div>
        <div className="flex flex-col w-7/12 h-full PRIMARY_50-BG px-3 py-3 rounded-2xl">
          <div className="h-5/6 relative  overflow-scroll flex flex-col items-center pb-3">
            <div
              style={{
                width: '612px',
                background: '#F4F7FA',
              }}
              className="sticky top-0  z-10 flex justify-between  mb-0.5"></div>

            <div
              style={{
                width: '612px',
                height: '792px',
              }}
              className="relative flex justify-center">
              {selectedPdfFile && (
                <Viewer
                  defaultScale={1}
                  fileUrl={selectedPdfFile.url}
                  plugins={[zoomPluginInstance, pageNavigationPluginInstance]}
                />
              )}
              {!selectedPdfFile && <p className="text-sm self-center ">No file selected</p>}
            </div>
          </div>

          <div className="flex flex-row justify-center">
            <div className="mx-4">
              <CurrentPageLabel>
                {(props: RenderCurrentPageLabelProps) => (
                  <span>{`page ${props.currentPage + 1} / ${props.numberOfPages}`}</span>
                )}
              </CurrentPageLabel>
            </div>

            <ZoomOutButton />
            <ZoomPopover />
            <ZoomInButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditApplicationManualUpload;
